import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Link,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  design?: string;
  completed_weight?: number;
  progress_percentage?: number;
  remaining_weight?: number;
  completed?: boolean;
}

interface Program {
  id: number;
  company: {
    id: number;
    name: string;
  };
  program_details: ProgramDetail[];
  lot?: string;
  status: "not_started" | "in_progress" | "completed" | "delivered";
  received_notes: Array<{
    id: number;
    total_bags: number;
    counts_display: string;
  }>;
  total_weight: number;
  specs_display: string;
  dias_display: string;
  gauges_display: string;
  types_display: string;
  designs_display: string;
  created_at: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

export default function ProgramList() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { canCreate, isReadOnly } = usePermissions();

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch programs
      const programsResponse = await authenticatedFetch(`${API_URL}/programs?page=${page}&per_page=25`);
      if (!programsResponse.ok) {
        throw new Error("Failed to fetch programs");
      }
      const programsData = await programsResponse.json();
      setPrograms(programsData);
      
      // Get pagination info from headers
      const totalPagesHeader = programsResponse.headers.get('X-Total-Pages');
      if (totalPagesHeader) {
        setTotalPages(parseInt(totalPagesHeader));
      }
      
      // Only fetch machines for users who have access (admin/employee)
      if (!isReadOnly) {
        const machinesResponse = await authenticatedFetch(`${API_URL}/machines`);
        if (machinesResponse.ok) {
          const machinesData = await machinesResponse.json();
          setMachines(machinesData);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getProgramProgress = (program: Program) => {
    if (program.status !== "in_progress") return null;

    let totalWeight = 0;
    let completedWeight = 0;

    program.program_details.forEach((detail) => {
      totalWeight += Number(detail.weight_in_kilo) || 0;
      completedWeight += Number(detail.completed_weight) || 0;
    });

    const percentage = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;
    return { percentage, completed: completedWeight, total: totalWeight };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {canCreate('programs') && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => navigate("/programs/new")}
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
              >
                {t('programs.createNew')}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isMobile ? (
            // Mobile Card View
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {programs.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {t('programs.noPrograms')}
                </Typography>
              ) : (
                programs.map((program) => {
                  const progress = getProgramProgress(program);
                  return (
                    <Card 
                      key={program.id}
                      onClick={() => navigate(`/programs/${program.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Typography variant="h6" component="div">
                            Program #{program.id}
                          </Typography>
                          {program.status === "in_progress" && progress ? (
                            <Chip
                              label={`${(progress.total - progress.completed).toFixed(1)} kg left`}
                              size="small"
                              sx={{ bgcolor: "#fdd835", color: "#000" }}
                            />
                          ) : (
                            <Chip
                              label={
                                program.status === "not_started"
                                  ? "Not Started"
                                  : program.status === "completed"
                                  ? "Completed"
                                  : "Delivered"
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  program.status === "not_started"
                                    ? "#9e9e9e"
                                    : program.status === "completed"
                                    ? "#66bb6a"
                                    : "#2196f3",
                                color: "#fff",
                              }}
                            />
                          )}
                        </Stack>
                        
                        <Link
                          component="button"
                          variant="body1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/companies/${program.company?.id}`);
                          }}
                          sx={{ 
                            color: 'primary.main', 
                            fontWeight: 500, 
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            display: 'block',
                            textAlign: 'left',
                            mb: 1,
                          }}
                        >
                          {program.company?.name}
                        </Link>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Stack spacing={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Dia:</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {program.program_details
                                .filter((detail, index, self) => 
                                  index === self.findIndex((d) => 
                                    d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                                  )
                                )
                                .map((detail, idx) => (
                                  <DiaCircle
                                    key={idx}
                                    dia={detail.dia}
                                    gauge={detail.gauge}
                                    machineType={detail.program_type}
                                    machines={machines}
                                    size="small"
                                  />
                                ))}
                            </Box>
                          </Box>
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Gauge:</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {(program.gauges_display || program.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                                .split(", ")
                                .filter(g => g)
                                .map((gauge, idx) => (
                                  <GaugeChip
                                    key={idx}
                                    gauge={gauge}
                                  />
                                ))}
                            </Box>
                          </Box>
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Type:</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {program.types_display
                                .split(", ")
                                .map((type, idx) => (
                                  <Chip
                                    key={idx}
                                    label={type}
                                    size="small"
                                    sx={{
                                      bgcolor: type === "rib" ? '#e0e0e0' : '#757575',
                                      color: type === "rib" ? '#000' : '#fff',
                                    }}
                                  />
                                ))}
                            </Box>
                          </Box>
                          
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Weight:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {typeof program.total_weight === "number"
                                ? program.total_weight.toFixed(2)
                                : Number(program.total_weight || 0).toFixed(2)} kg
                            </Typography>
                          </Box>
                          
                          {program.designs_display && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Design:</Typography>
                              <Typography variant="body2">{program.designs_display}</Typography>
                            </Box>
                          )}
                          
                          {program.lot && (
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Lot:</Typography>
                              <Typography variant="body2">{program.lot}</Typography>
                            </Box>
                          )}
                          
                          {program.received_notes && program.received_notes.length > 0 && (
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" color="text.secondary">Received Notes:</Typography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {program.received_notes.map((receivedNote) => (
                                  <Chip
                                    key={receivedNote.id}
                                    label={`#${receivedNote.id}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Stack>
                        
                        {program.status === "in_progress" && progress && (
                          <Box sx={{ mt: 2 }}>
                            <ProgressBar
                              type="weight"
                              done={progress.completed}
                              total={progress.total}
                              size="small"
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Status</TableCell>
                  <TableCell>Dia</TableCell>
                  <TableCell>Gauge (GG)</TableCell>
                  <TableCell>Type(s)</TableCell>
                  <TableCell>Total Weight (kg)</TableCell>
                  <TableCell>Design(s)</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell>Received Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {t('programs.noPrograms')}
                    </TableCell>
                  </TableRow>
                ) : (
                  programs.map((program) => (
                    <TableRow
                      key={program.id}
                      onClick={() => navigate(`/programs/${program.id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <TableCell>{program.id}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/companies/${program.company?.id}`);
                          }}
                          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {program.company?.name}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {program.status === "in_progress" ? (
                          (() => {
                            const progress = getProgramProgress(program);
                            return progress ? (
                              <ProgressBar
                                type="weight"
                                done={progress.completed}
                                total={progress.total}
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="In Progress"
                                size="small"
                                sx={{ bgcolor: "#fdd835", color: "#000" }}
                              />
                            );
                          })()
                        ) : (
                          <Chip
                            label={
                              program.status === "not_started"
                                ? "Not Started"
                                : program.status === "completed"
                                ? "Completed"
                                : "Delivered"
                            }
                            size="small"
                            sx={{
                              bgcolor:
                                program.status === "not_started"
                                  ? "#9e9e9e"
                                  : program.status === "completed"
                                  ? "#66bb6a"
                                  : "#2196f3",
                              color: "#fff",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {program.program_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.program_type}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(program.gauges_display || program.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {program.types_display
                            .split(", ")
                            .map((type, idx) => (
                              <Chip
                                key={idx}
                                label={type}
                                size="small"
                                sx={{
                                  bgcolor: type === "rib" ? '#e0e0e0' : '#757575',
                                  color: type === "rib" ? '#000' : '#fff',
                                }}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {typeof program.total_weight === "number"
                          ? program.total_weight.toFixed(2)
                          : Number(program.total_weight || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>{program.designs_display}</TableCell>
                      <TableCell>{program.lot || "-"}</TableCell>
                      <TableCell>
                        {program.received_notes && program.received_notes.length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {program.received_notes.map((receivedNote) => (
                              <Chip
                                key={receivedNote.id}
                                label={`#${receivedNote.id}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
